import { useState } from 'react';

import {
  AssessmentAttemptResultPreview,
  OffsetPagination,
} from '@opositandopn/ui';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';

import { Empty } from '../../../../components/core/Empty';
import { Loading } from '../../../../components/core/Loading';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { formatHourDate, lastMonthsRange } from '../../../../utils/date';
import { useGetMultipleChoiceAssessmentAttempts } from '../../api/useMultipleChoiceAssessments';

import styles from './MultipleChoiceAssessmentAttemptList.module.scss';

type ContainerProps = BaseComponentProps & {
  subjectId: string;
};

function MultipleChoiceAssessmentAttemptList({
  className,
  subjectId,
  testId = 'ui-multiple-choice-assessment-attempt-list',
}: ContainerProps) {
  const navigate = useNavigate();

  const [dateRange] = useState<[Date, Date]>(() => lastMonthsRange(12));
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 10 });

  const { data: response, isLoading } = useGetMultipleChoiceAssessmentAttempts({
    subjectId: subjectId,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    fromTime: dateRange[0],
    toTime: dateRange[1],
    sort: '-createdAt',
    params: {
      statuses: ['submitted', 'overdue'],
    },
  });

  if (isLoading || !response) {
    return <Loading />;
  }

  if (response.totalItems === 0) {
    return <Empty title="No tienes simulacros completados" />;
  }

  return (
    <div className={classnames(styles['main'], className)} data-testid={testId}>
      {response.items.map((item) => {
        const totalQuestions =
          item.questionAttemptsSummary!.correct +
          item.questionAttemptsSummary!.incorrect +
          item.questionAttemptsSummary!.skipped;

        const description = item.lessons?.length
          ? `${totalQuestions} ${totalQuestions === 1 ? 'pregunta' : 'preguntas'} de ${item.lessons.length} ${item.lessons.length === 1 ? 'tema' : 'temas'}`
          : `${totalQuestions} ${totalQuestions === 1 ? 'pregunta' : 'preguntas'}`;

        return (
          <AssessmentAttemptResultPreview
            className={styles['item']}
            key={item.id}
            title={item.name}
            score={item.score ?? 0}
            elapsedTime={item.elapsedTime}
            description={description}
            answerRate={{
              correct: item.questionAttemptsSummary!.correct,
              incorrect: item.questionAttemptsSummary!.incorrect,
              skipped: item.questionAttemptsSummary!.skipped,
            }}
            submittedDate={formatHourDate(item.createdAt, item.timeZoneId)}
            onClick={() =>
              navigate(
                `/materia/${subjectId}/simulacros/mc/resultados/${item.id}`,
              )
            }
          />
        );
      })}
      <OffsetPagination
        pageNumber={pagination.pageNumber}
        pageSize={pagination.pageSize}
        totalRecords={response.totalItems}
        onPageChange={(pageNumber: number) =>
          setPagination((prev) => ({ ...prev, pageNumber }))
        }
        pageNeighbours={2}
      />
    </div>
  );
}

export { MultipleChoiceAssessmentAttemptList };
